import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import api from 'api';
import Icons from 'components/Icons';
import { useDebouncedCallback } from 'use-debounce';

const useStyles = makeStyles({
  '@global': {
    '.MuiAutocomplete-popup': {
      zIndex: 1501
    },
    '.MuiAutocomplete-option': {
      fontSize: '13px',
      transform: 'translateZ(0)',
      backfaceVisibility: 'hidden'
    },
    '.MuiAutocomplete-popupIndicator': {
      display: 'none'
    }
  },
  search: {
    width: '100%',
    backgroundColor: '#e5e5e5',
    borderRadius: '25px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    padding: '0.2rem 0.8rem 0 2.4rem'
  },
  input: {
    '&:before': {
      border: '0 !important',
      paddingLeft: '0.8rem'
    }
  },
  searchContainer: {
    width: '100%',
    position: 'relative'
  },
  searchIcon: {
    position: 'absolute',
    left: '8px',
    top: '10px'
  }
});

interface SearchTypes {
  onSelect: (val: string) => any;
  inputId?: string;
  isLoadingProp?: boolean;
}

const ModelPortfolioSearch = ({
  onSelect,
  inputId,
  isLoadingProp = false
}: SearchTypes) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(isLoadingProp);
  const [searchValue, changeSearchValue] = useState('');
  const classes = useStyles();

  const search = useDebouncedCallback(() => {
    api
      .getModelPortfolioSelections({ page: 1, search: searchValue })
      .then(({ data }: any) => {
        if (data.results.length > 0) {
          setOpen(true);
        }
        setOptions(data.results);
        setIsLoading(false);
      });
  }, 500);

  useEffect(() => {
    if (searchValue.length < 2) {
      setOptions([]);
      setOpen(false);
    }
    if (searchValue.length > 1) {
      setIsLoading(true);
      search();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  useEffect(() => {
    setIsLoading(isLoadingProp);
  }, [isLoadingProp]);

  return (
    <div className={classes.searchContainer}>
      <Icons.SearchIcon className={classes.searchIcon} />
      <Autocomplete
        className={classes.search}
        open={open}
        id={inputId}
        onOpen={() => {
          if (options.length > 0) {
            setOpen(true);
          }
        }}
        onClose={() => {
          setOpen(false);
        }}
        disableClearable={true}
        getOptionLabel={(option: any) => option.name}
        options={options}
        onChange={(ev: any, val: any) => {
          onSelect(val);
          setOpen(false);
          changeSearchValue('');
        }}
        loading={isLoading}
        renderInput={(params: any) => {
          return (
            <TextField
              {...params}
              placeholder="Select Portfolio"
              fullWidth
              variant="standard"
              inputProps={{
                ...params.inputProps,
                onChange: (ev: any) => {
                  changeSearchValue(ev.target.value);
                },
                value: searchValue,
                autoComplete: 'new-password'
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
                className: classes.input
              }}
            ></TextField>
          );
        }}
      />
    </div>
  );
};

export default ModelPortfolioSearch;
